import React from 'react';
import PropTypes from 'prop-types';
import { FaBuilding, FaCity, FaMapMarkerAlt, FaMapSigns } from 'react-icons/fa';

import InputTextNested from '../../../Components/Form/InputTextNested';
import ProvinceInput from '../../../Components/Form/ProvinceInput.component';
import PrimaryButtonComponent from '../../../Components/Buttons/PrimaryButton/PrimaryButton.component';

import {
  CompanyAddressContainer,
  CompanyAddressHeader,
  CompanyAddressHeaderIcon,
  CompanyAddressHeaderContent,
  CompanyAddressOptional,
  CompanyAddressFields,
  CompanyAddressFieldRow,
  CompanyAddressNotice,
  CompanyAddressNoticeIcon,
  CompanyAddressNoticeContent,
  CompanyAddressActions,
  CompanyAddressSecondaryActions,
  CompanyAddressBackButton,
  CompanyAddressSkipButton,
} from './CompanyAddress.styles';

const CompanyAddress = ({ billingAddress, setBillingAddress, setStep }) => {
  const hasAddressStarted =
    billingAddress?.street?.trim() ||
    billingAddress?.city?.trim() ||
    billingAddress?.province?.trim() ||
    billingAddress?.postalCode?.trim();

  const isAddressComplete =
    billingAddress?.street?.trim() &&
    billingAddress?.city?.trim() &&
    billingAddress?.province?.trim() &&
    billingAddress?.postalCode?.trim();

  const canContinue = !hasAddressStarted || isAddressComplete;

  const handleBack = () => {
    setStep((prev) => prev - 1);
  };

  const handleSkip = () => {
    setBillingAddress({
      street: '',
      city: '',
      province: '',
      postalCode: '',
    });

    setStep((prev) => prev + 1);
  };

  const handleContinue = () => {
    if (!canContinue) return;

    const trimmedAddress = {
      street: billingAddress?.street?.trim() || '',
      city: billingAddress?.city?.trim() || '',
      province: billingAddress?.province?.trim() || '',
      postalCode: billingAddress?.postalCode?.trim() || '',
    };

    setBillingAddress(trimmedAddress);

    setStep((prev) => prev + 1);
  };

  return (
    <CompanyAddressContainer>
      <CompanyAddressHeader>
        <CompanyAddressHeaderIcon>
          <FaMapMarkerAlt />
        </CompanyAddressHeaderIcon>

        <CompanyAddressHeaderContent>
          <div>
            <span>Company Address</span>

            <CompanyAddressOptional>Optional</CompanyAddressOptional>
          </div>

          <h2>Where Is Your Company Located?</h2>

          <p>
            Add your company&apos;s primary business address for your company
            records. If you don&apos;t have this information handy, you can add
            it later.
          </p>
        </CompanyAddressHeaderContent>
      </CompanyAddressHeader>

      <CompanyAddressNotice>
        <CompanyAddressNoticeIcon>
          <FaBuilding />
        </CompanyAddressNoticeIcon>

        <CompanyAddressNoticeContent>
          <strong>You can come back to this later</strong>

          <p>
            Your address isn&apos;t required to create your company account.
            You&apos;ll be able to add or update it from your company profile.
          </p>
        </CompanyAddressNoticeContent>
      </CompanyAddressNotice>

      <CompanyAddressFields>
        <InputTextNested
          title={
            <>
              <FaMapSigns />
              Street Address
            </>
          }
          value={billingAddress?.street}
          name="street"
          placeholder="123 Main Street"
          nestedValue={setBillingAddress}
          upperValue={billingAddress}
          containerClassName="formInputUnderline"
        />

        <CompanyAddressFieldRow>
          <InputTextNested
            title={
              <>
                <FaCity />
                City
              </>
            }
            name="city"
            value={billingAddress?.city}
            placeholder="Halifax"
            upperValue={billingAddress}
            nestedValue={setBillingAddress}
            containerClassName="formInputUnderline"
          />

          <ProvinceInput
            title="Province"
            name="province"
            value={billingAddress?.province}
            upperValue={billingAddress}
            nestedValue={setBillingAddress}
            containerClassName="formInputUnderline"
          />
        </CompanyAddressFieldRow>

        <InputTextNested
          title={
            <>
              <FaMapMarkerAlt />
              Postal Code
            </>
          }
          name="postalCode"
          value={billingAddress?.postalCode}
          placeholder="B3H 1A1"
          upperValue={billingAddress}
          nestedValue={setBillingAddress}
          containerClassName="formInputUnderline"
        />
      </CompanyAddressFields>

      {hasAddressStarted && !isAddressComplete && (
        <CompanyAddressNotice $warning>
          <CompanyAddressNoticeIcon $warning>
            <FaMapMarkerAlt />
          </CompanyAddressNoticeIcon>

          <CompanyAddressNoticeContent>
            <strong>Finish the address to continue</strong>

            <p>
              Complete all address fields, or choose Skip for Now to leave the
              address blank and add it later.
            </p>
          </CompanyAddressNoticeContent>
        </CompanyAddressNotice>
      )}

      {setStep && (
        <CompanyAddressActions>
          <CompanyAddressSecondaryActions>
            <CompanyAddressBackButton type="button" onClick={handleBack}>
              Back
            </CompanyAddressBackButton>

            <CompanyAddressSkipButton type="button" onClick={handleSkip}>
              Skip for Now
            </CompanyAddressSkipButton>
          </CompanyAddressSecondaryActions>

          <PrimaryButtonComponent
            span="Continue to Review"
            functionName={handleContinue}
            disabled={!canContinue}
          />
        </CompanyAddressActions>
      )}
    </CompanyAddressContainer>
  );
};

CompanyAddress.propTypes = {
  billingAddress: PropTypes.shape({
    street: PropTypes.string,
    city: PropTypes.string,
    province: PropTypes.string,
    postalCode: PropTypes.string,
  }),
  setBillingAddress: PropTypes.func,
  setStep: PropTypes.func,
};

export default CompanyAddress;
